// A topic's members (CHAT-TOPICS-SEPARATE): who is notified of it and counts it unread — its
// opener, the people and agents added, anyone who posts there and anyone @mentioned there (the
// API adds those itself). An open topic is listed to the channel and joined with one click; a
// non-member sees its name, never its messages (the join prompt). A private one is seen by its
// members only; its opener or a manager of the channel sets that. The members modal opens from
// the visible icon in the topic's header: add, remove (yourself: leave), and the Private toggle.
import {
  AvatarTile,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  InfoTip,
  Switch,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HashIcon, LockIcon, UsersIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { problemText } from "../ops-parts";
import {
  type PickerItem,
  SearchList,
  useChannelMembers,
  useManagesChannel,
} from "./channel-agents";
import { useCanManage } from "./channel-settings";
import {
  type Channel,
  chatKeys,
  type TopicMember,
  type TopicSummary,
  topicMembersQuery,
} from "./chat-queries";
import type { ChatContext } from "./use-chat";

export type TopicMembersRef = Pick<
  TopicSummary,
  "id" | "name" | "channel_id" | "is_private" | "is_opener" | "is_member"
> & { member_count?: number | undefined };

type ChannelRef = Pick<Channel, "id" | "name" | "archived_at" | "is_private">;

/** Refreshes what a membership change touches: the members, the topic, the channel's lists. */
function useTopicRefresh(chat: ChatContext, topic: Pick<TopicSummary, "id" | "channel_id">) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: chatKeys.topic(chat.co, topic.id) }),
      queryClient.invalidateQueries({ queryKey: chatKeys.topics(chat.co, topic.channel_id) }),
      queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) }),
    ]);
}

/** Adds or removes topic members (joining is adding yourself, leaving removing yourself). */
export function useUpdateTopicMembers(
  chat: ChatContext,
  topic: Pick<TopicSummary, "id" | "channel_id">,
) {
  const refresh = useTopicRefresh(chat, topic);
  return useMutation({
    mutationFn: (body: { add?: string[]; remove?: string[] }) =>
      chat.api.client.topics.updateMembers({ params: { co: chat.co, id: topic.id }, body }),
    onSuccess: () => void refresh(),
    onError: (e) =>
      toast.add({
        title: "That did not go through",
        description: problemText(e, e.message),
        type: "error",
      }),
  });
}

/** Whether the caller may make the topic private or open: its opener or a channel manager. */
export function useCanSetPrivate(
  chat: ChatContext,
  channel: ChannelRef,
  topic: Pick<TopicSummary, "is_opener">,
): boolean {
  const fallback = useCanManage(chat, channel);
  const manages = useManagesChannel(chat, channel, fallback);
  if (chat.meId === null || chat.readOnly) return false;
  // The opener is the API's `creator` row (the topic's actor columns name its last editor).
  return manages || topic.is_opener;
}

/**
 * An open topic the caller is not in: its name, how many are in it and "Join topic" — no
 * messages, no composer. Joining shows the conversation.
 */
export function TopicJoinPrompt({ chat, topic }: { chat: ChatContext; topic: TopicMembersRef }) {
  const update = useUpdateTopicMembers(chat, topic);
  const me = chat.meId;
  const count = topic.member_count ?? 0;
  return (
    <section
      data-slot="topic-join"
      aria-label={`Join topic: ${topic.name}`}
      className="m-auto flex flex-col items-center gap-2 p-8 text-center"
    >
      <HashIcon aria-hidden="true" className="size-6 text-faint" />
      <p className="m-0 text-[15px] font-semibold text-foreground">{topic.name}</p>
      <p className="m-0 text-sm text-muted-foreground">
        {count === 1 ? "1 member" : `${count} members`}
      </p>
      {me !== null && !chat.readOnly ? (
        <Button
          size="sm"
          className="mt-1"
          disabled={update.isPending}
          onClick={() => update.mutate({ add: [me] })}
        >
          Join topic
        </Button>
      ) : null}
    </section>
  );
}

/** The header's visible members icon (with the count) that opens the members modal. */
export function TopicMembersButton({
  chat,
  channel,
  topic,
}: {
  chat: ChatContext;
  channel: ChannelRef;
  topic: TopicMembersRef;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size="xs"
        aria-label={`Topic members: ${topic.name}`}
        title="Topic members"
        className="text-secondary"
        onClick={() => setOpen(true)}
      >
        <UsersIcon data-icon="inline-start" />
        {topic.member_count !== undefined ? (
          <span className="tabular-nums">{topic.member_count}</span>
        ) : null}
      </Button>
      {open ? (
        <TopicMembersDialog
          chat={chat}
          channel={channel}
          topic={topic}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

/** The members modal: the list (remove / leave), add people and agents, the Private toggle. */
export function TopicMembersDialog({
  chat,
  channel,
  topic,
  onClose,
}: {
  chat: ChatContext;
  channel: ChannelRef;
  topic: TopicMembersRef;
  onClose: () => void;
}) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  const members = useQuery({ ...topicMembersQuery(api, co, topic.id), retry: false });
  const fallback = useCanManage(chat, channel);
  const manages = useManagesChannel(chat, channel, fallback);
  const canSetPrivate = useCanSetPrivate(chat, channel, topic);
  const update = useUpdateTopicMembers(chat, topic);
  const refresh = useTopicRefresh(chat, topic);
  // A private channel's topic takes only the channel's members.
  const channelMembers = useChannelMembers(chat, channel, channel.is_private);
  const privacy = useMutation({
    mutationFn: (on: boolean) =>
      api.client.topics.privacy({ params: { co, id: topic.id }, body: { private: on } }),
    onSuccess: (_r, on) => {
      void refresh();
      toast.add({ title: on ? "Topic is private" : "Topic is open", type: "success" });
    },
    onError: (e) =>
      toast.add({
        title: "That did not go through",
        description: problemText(e, e.message),
        type: "error",
      }),
  });
  const write = chat.meId !== null && !chat.readOnly;
  const rows: TopicMember[] = members.data?.data ?? [];
  const ids = new Set(rows.map((m) => m.membership_id));
  const isMember = chat.meId !== null && ids.has(chat.meId);
  const isOpener = rows.some((r) => r.membership_id === chat.meId && r.via === "creator");
  const canAdd = write && (isMember || manages);
  const canRemoveOthers = write && (isOpener || manages);
  const nameOf = (id: string) => {
    const m = chat.members.get(id);
    return m ? (m.kind === "de" ? workerName(m.name) : m.name) : "Someone";
  };
  const candidates: PickerItem[] = [...chat.members.values()]
    .filter(
      (m) =>
        !ids.has(m.id) &&
        m.deStatus !== "retired" &&
        (!channel.is_private || channelMembers.ids.has(m.id)),
    )
    .sort((a, b) => workerName(a.name).localeCompare(workerName(b.name)))
    .map((m) => ({
      id: m.id,
      text: m.kind === "de" ? workerName(m.name) : m.name,
      render: (
        <>
          <AvatarTile
            name={workerName(m.name)}
            kind={m.kind === "de" ? "agent" : "person"}
            size="xs"
            aria-hidden="true"
          />
          <span className="min-w-0 truncate">{m.kind === "de" ? workerName(m.name) : m.name}</span>
          {m.kind === "de" ? <WorkerBadge kind="de" size="sm" /> : null}
        </>
      ),
      ...(m.deStatus === "paused" || m.deStatus === "not_deployed"
        ? { disabledReason: m.deStatus === "paused" ? "Paused" : "Not deployed" }
        : {}),
    }));
  const remove = (id: string) =>
    update.mutate(
      { remove: [id] },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: chatKeys.topicMembers(co, topic.id) });
          if (id === chat.meId) {
            toast.add({ title: `Left "${topic.name}"`, type: "success" });
            onClose();
          }
        },
      },
    );
  const add = (id: string) =>
    update.mutate(
      { add: [id] },
      {
        onSuccess: () =>
          void queryClient.invalidateQueries({ queryKey: chatKeys.topicMembers(co, topic.id) }),
      },
    );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-slot="topic-members">
        <DialogHeader>
          <DialogTitle>Members of {topic.name}</DialogTitle>
        </DialogHeader>
        {canSetPrivate ? (
          <div className="flex items-center gap-2">
            <LockIcon aria-hidden="true" className="size-4 text-faint" />
            <label htmlFor="topic-private" className="text-sm font-medium text-foreground">
              Private
            </label>
            <InfoTip label="About private topics">
              Only its members see a private topic. Open topics are listed in the channel and anyone
              there can join.
            </InfoTip>
            <Switch
              id="topic-private"
              aria-label="Private"
              className="ml-auto"
              checked={topic.is_private}
              disabled={privacy.isPending}
              onCheckedChange={(on) => privacy.mutate(on)}
            />
          </div>
        ) : topic.is_private ? (
          <p className="m-0 flex items-center gap-1.5 text-sm text-muted-foreground">
            <LockIcon aria-hidden="true" className="size-3.5" />
            Private
          </p>
        ) : null}
        {members.isError ? (
          <p role="alert" className="m-0 text-sm text-muted-foreground">
            Could not load the members.
          </p>
        ) : members.isPending ? (
          <p role="status" className="m-0 text-sm text-muted-foreground">
            Loading…
          </p>
        ) : (
          <ul
            aria-label="Topic members"
            className="m-0 flex max-h-64 list-none flex-col overflow-y-auto p-0"
          >
            {rows.map((r) => {
              const m = chat.members.get(r.membership_id);
              const self = r.membership_id === chat.meId;
              const removable = write && (self || canRemoveOthers);
              return (
                <li
                  key={r.membership_id}
                  data-member={r.membership_id}
                  className="group/member flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <AvatarTile
                    name={nameOf(r.membership_id)}
                    kind={m?.kind === "de" ? "agent" : "person"}
                    size="xs"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 truncate">
                    {nameOf(r.membership_id)}
                    {self ? <span className="text-muted-foreground"> (you)</span> : null}
                  </span>
                  {m?.kind === "de" ? <WorkerBadge kind="de" size="sm" /> : null}
                  {removable ? (
                    self ? (
                      <Button
                        variant="ghost"
                        size="xs"
                        className="ml-auto text-secondary"
                        disabled={update.isPending}
                        onClick={() => remove(r.membership_id)}
                      >
                        Leave
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Remove ${nameOf(r.membership_id)}`}
                        title="Remove"
                        className="ml-auto text-faint hover:text-foreground"
                        disabled={update.isPending}
                        onClick={() => remove(r.membership_id)}
                      >
                        <XIcon />
                      </Button>
                    )
                  ) : null}
                </li>
              );
            })}
            {!rows.length ? (
              <li className="px-2 py-1.5 text-sm text-muted-foreground">No members yet.</li>
            ) : null}
          </ul>
        )}
        {canAdd ? (
          <div className="flex flex-col gap-1.5 border-t border-border-subtle pt-3">
            <span className="text-xs font-medium text-muted-foreground">Add people or agents</span>
            <SearchList
              label="Add to topic"
              items={candidates}
              empty="Everyone is in this topic."
              placeholder="Search people and agents…"
              status={channel.is_private && channelMembers.members.isPending ? "loading" : "ready"}
              onPick={add}
            />
          </div>
        ) : write && !isMember && !members.isPending && !topic.is_private ? (
          <Button
            size="sm"
            disabled={update.isPending || chat.meId === null}
            onClick={() => chat.meId && add(chat.meId)}
          >
            Join topic
          </Button>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
